import { Formik } from "formik";
import * as _ from "lodash";
import * as React from "react";
import { toast } from "react-toastify";
import * as yup from "yup";
import styled from "styled-components";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";

import {
  Form,
  IFormField
} from "src/library/components/Forms";
import { Modal } from "src/library/components/Modal";
import { Text } from "src/library/components/Text";
import Grid from "src/library/components/Hedron";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import {
  useProductsApi,
  IProductCategory
} from "src/core/products/api/products.api";
import { PageProps } from "src/pages/Router/routes";
import useCheckUserPermission from "src/core/users/hooks/useCheckUserPermission";
import { CategoryDraggableCard } from "src/core/products/components/CategoryDraggableCard";
import {
  FixedBottomMenu,
  FixedBottomMenuWrapper
} from "src/core/shared/components/menus/FixedBottomMenu";
import { CategoryUneditableCard } from "src/core/products/components/CategoryUneditableCard";

interface IModal {
  id: number;
  name: string;
}

/**
 * Main "Product Categories" View
 *
 * @returns
 */
const ProductCategoriesPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { terminology } = props;
  const [ listCategoriesResponse, getProductCategories ] = useProductsApi("LIST_PRODUCT_CATEGORIES");
  const [ createCategoryResponse, createProductCategory ] = useProductsApi("CREATE_PRODUCT_CATEGORY");
  const [ deleteCategoryResponse, deleteProductCategory ] = useProductsApi("DELETE_PRODUCT_CATEGORY");
  const [ bulkUpdateCategorySortOrderRes, bulkUpdateCategorySortOrderReq ] = useProductsApi("BULK_UPDATE_CATEGORY_SORT_ORDER");
  const [ showModal, setShowModal ] = React.useState<IModal | null>(null);
  const [ currentCategoriesData, setCurrentCategoriesData ] = React.useState<IProductCategory[]>([]);
  const [ sortOrderChanged, setSortOrderChanged ] = React.useState(false);
  const productCategoriesData = listCategoriesResponse.data;
  const checkPermission = useCheckUserPermission();
  const canCreateCategory = checkPermission("create-product-categories");
  const canAmendCategoires = checkPermission("amend-product-categories");

  React.useEffect(() => {
    // get product categories on mount
    getProductCategories({
      filters: {},
      branchId: null
    });
  }, [ getProductCategories ]);

  React.useEffect(() => {
    // transform categories data to display when ever new set fetched
    if (listCategoriesResponse.data) {
      const parentsOnly = _.filter(listCategoriesResponse.data, cateory => !cateory.parentId);

      // ensure order by sortOrder then sanitise sortOrder values
      setCurrentCategoriesData(_.map(_.orderBy(parentsOnly, category => category.sortOrder), (category, index) => ({
        ...category,
        sortOrder: index + 1
      })));
    }
  }, [ listCategoriesResponse ]);

  const formRef = React.useRef<Formik>(null);

  const createCategoryForm: () => IFormField[] = () => [
    {
      type: "input",
      name: "name",
      label: `${terminology.category.singular.text} name`,
      placeholder: `${terminology.category.singular.text} name`,
      validation: yup
        .string()
        .min(1)
        .required(`${terminology.category.singular.text} name is required`)
    },
    {
      type: "select",
      name: "parentId",
      label: `Parent ${terminology.category.singular.text}`,
      options: productCategoriesData ?
        _.map(productCategoriesData.filter(category => !category.parentId), category => ({
          value: category.id.toString(),
          text: category.name
        })) :
        []
    }
  ];

  React.useEffect(() => {
    // On Success reset the form
    if (createCategoryResponse.data) {
      if (formRef.current) {
        formRef.current.resetForm();
      }

      getProductCategories({
        filters: {},
        branchId: null
      });
      toast.success(`Your ${terminology.category.singular.text} has been successfully created.`);
    }
  }, [ createCategoryResponse.data, getProductCategories ]);

  React.useEffect(() => {
    // on success reload catigories data
    if (deleteCategoryResponse.data) {
      getProductCategories({
        filters: {},
        branchId: null
      });
      toast.success(`Your ${terminology.category.singular.text} has been successfully deleted.`);
    }

    // catch error, display according to response status
    if (deleteCategoryResponse.error) {
      if (deleteCategoryResponse.error.status === 409) {
        toast.error(
          `This ${terminology.category.singular.text} is associated with other products. Please remove the category from these products and try again`
        );
      } else {
        toast.error(`Could not delete ${terminology.category.singular.text} - ${deleteCategoryResponse.error.message}`);
      }
    }
  }, [ deleteCategoryResponse, getProductCategories ]);

  React.useEffect(() => {
    // handle a buld categories sort order api response
    if (bulkUpdateCategorySortOrderRes.data) {
      toast.success("Categories sort order saved!");
      setSortOrderChanged(false);
    } else if (bulkUpdateCategorySortOrderRes.error) {
      toast.error(bulkUpdateCategorySortOrderRes.error.message);
    }
  }, [ bulkUpdateCategorySortOrderRes ]);

  // update categories sort orders when dragging a card
  const moveCard = React.useCallback((dragIndex: number, hoverIndex: number) => {
    setCurrentCategoriesData(prevCategories => {
      if (dragIndex) {
        prevCategories[ hoverIndex - 1 ].sortOrder = dragIndex;
        prevCategories[ dragIndex - 1 ].sortOrder = hoverIndex;

        return _.orderBy(prevCategories, category => category.sortOrder);
      }

      return prevCategories;
    });
  }, []);

  // use a given category id to return all relevant child categories
  const getChildCategories = (categoryId: number) => {
    const childCategories = _.filter(listCategoriesResponse.data, childCategory => childCategory.parentId === categoryId);

    return _.orderBy(childCategories, category => category.name);
  };

  return (
    <Layout {...props}>
      <MainContentWrapper fill>
        <Grid.Bounds style={{ flexWrap: "wrap" }}>
          {canCreateCategory && (
            <NewCategoryWrapper>
              <Text
                fontSize="h3"
                weight="bold"
              >
                {`Create a new ${terminology.product.singular.text} ${terminology.category.singular.text}`}
              </Text>

              <FormWrapper>
                <Form
                  ref={formRef}
                  error={createCategoryResponse.error ? createCategoryResponse.error.message : null}
                  submitting={createCategoryResponse.pending}
                  fields={createCategoryForm()}
                  onSubmit={({ name, parentId }) =>
                    createProductCategory({
                      name,
                      parentId: parentId || null
                    })}
                  submitButtonText={`Create ${terminology.category.singular.text}`}
                />
              </FormWrapper>
            </NewCategoryWrapper>
          )}

          <CategoriesWrapper fill>
            <Text
              fontSize="h3"
              weight="bold"
            >
              {`All ${terminology.category.plural.text}`}
            </Text>

            {listCategoriesResponse.pending && (
              <LoadingSpinner
                type="flat"
                position="inline"
              />
            )}

            <DndProvider backend={HTML5Backend}>
              <CategoriesList>
                {canAmendCategoires ?
                  _.map(currentCategoriesData, category => (
                    <CategoryDraggableCard
                      key={`category-${category.id}`}
                      name={category.name}
                      sortOrder={category.sortOrder || 0}
                      id={category.id}
                      moveCard={moveCard}
                      terminology={terminology}
                      sortOrderChanged={sortOrderChanged}
                      updateSortOrderChanged={setSortOrderChanged}
                      childCategories={getChildCategories(category.id)}
                      onDeleteCategory={category => setShowModal({
                        id: category.id,
                        name: category.name
                      })}
                    />
                  )) :
                  _.map(currentCategoriesData, category => (
                    <CategoryUneditableCard
                      key={`category-${category.id}`}
                      terminology={terminology}
                      childCategories={getChildCategories(category.id)}
                      name={category.name}
                    />
                  ))}
              </CategoriesList>
            </DndProvider>

          </CategoriesWrapper>

        </Grid.Bounds>

        <FixedBottomMenuWrapper active={sortOrderChanged}>
          <FixedBottomMenu
            onCloseText="Discard Changes"
            onClose={() => {
              setSortOrderChanged(false);

              getProductCategories({
                filters: {},
                branchId: null
              });
            }}
            actions={[
              {
                $buttonType: "primary",
                loading: bulkUpdateCategorySortOrderRes.pending,
                name: "Save Categories",
                onClick: () => bulkUpdateCategorySortOrderReq(_.map(currentCategoriesData, category => ({
                  id: category.id,
                  sortOrder: (category.sortOrder || 0) + 1
                })))
              }
            ]}
          />
        </FixedBottomMenuWrapper>

        <Modal
          small
          show={showModal !== null}
          type="confirm"
          title="Are you sure?"
          body={showModal ?
            `Please confirm that you wish to permanently delete the ${showModal.name} ${
              terminology.category.singular.text
            } and any sub-categories which might be associated with it.` :
            `Please confirm that you wish to permanently delete the ${terminology.category.singular.text}`}
          actions={[
            {
              name: "No, cancel",
              $buttonType: "secondary",
              action: () => setShowModal(null)
            },
            {
              name: "I confirm",
              $buttonType: "primary",
              action: () => {
                if (showModal) {
                  setShowModal(null);
                  deleteProductCategory(showModal.id);
                }
              }
            }
          ]}
          backgroundAction={() => setShowModal(null)}
        />
      </MainContentWrapper>
    </Layout>
  );
};

const FormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: flex-start;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
  form {
    min-width: 350px;
  }
`;

const NewCategoryWrapper = styled(Grid.Box)`
  padding: 0 15px;
  margin-bottom: 25px;
`;

const CategoriesWrapper = styled(Grid.Box)`
  padding: 0 15px;
`;

const CategoriesList = styled.div`
  margin-top: ${({ theme }) => theme.spacing.margins.xl};
`;

export default ProductCategoriesPage;
